import { Input } from "./input";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import React from "react";
import "./styles/controlled-number-field.css";

// Generic ControlledNumberField for use with react-hook-form
export type ControlledNumberFieldProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  "control"
> &
  Omit<
    React.ComponentProps<"input">,
    "name" | "value" | "onChange" | "ref" | "type"
  > & {
    control: UseControllerProps<T>["control"];
    label?: string;
    helperText?: string;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    min?: number;
    max?: number;
    step?: number;
    allowDecimals?: boolean;
  };

export function ControlledNumberField<T extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  helperText,
  startAdornment,
  endAdornment,
  min,
  max,
  step = 1,
  allowDecimals = true,
  ...inputProps
}: ControlledNumberFieldProps<T>) {
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
  } = useController<T>({
    control: control!,
    name,
    ...(defaultValue !== undefined && { defaultValue }),
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
        isNumber: (value) => {
          if (value === "" || value === undefined || value === null)
            return true;
          const num = Number(value);
          if (isNaN(num)) return "Please enter a valid number";
          if (min !== undefined && num < min)
            return `Value must be at least ${min}`;
          if (max !== undefined && num > max)
            return `Value must be at most ${max}`;
          if (!allowDecimals && num % 1 !== 0)
            return "Decimal values are not allowed";
          return true;
        },
      },
    },
    ...(shouldUnregister !== undefined && { shouldUnregister }),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty string
    if (inputValue === "") {
      onChange("");
      return;
    }

    // Allow negative sign at the beginning
    if (inputValue === "-") {
      onChange("-");
      return;
    }

    // Allow decimal point for decimal numbers
    if (allowDecimals && inputValue.endsWith(".")) {
      onChange(inputValue);
      return;
    }

    // Validate the input and convert to number
    const num = Number(inputValue);
    if (!isNaN(num)) {
      onChange(num as any); // Store as number, not string
    }
  };

  const hasStart = Boolean(startAdornment);
  const hasEnd = Boolean(endAdornment);

  const inputClassName = [
    hasStart ? "form-input--with-start" : "",
    hasEnd ? "form-input--with-end" : "",
    inputProps.className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`form-field controlled-number-field ${
        error ? "form-field--error" : ""
      }`}
    >
      {label && (
        <span
          className={`form-field__label ${
            inputProps.required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}

      <div className="form-field__container controlled-number-field__container">
        {hasStart && (
          <div className="form-adornment form-adornment--start controlled-number-field__adornment controlled-number-field__adornment--start">
            {startAdornment}
          </div>
        )}

        <Input
          {...inputProps}
          {...field}
          value={value ?? ""}
          onChange={handleChange}
          type="text"
          inputMode="numeric"
          pattern={allowDecimals ? "[0-9]*.?[0-9]*" : "[0-9]*"}
          className={inputClassName}
          min={min}
          max={max}
          step={step}
        />

        {hasEnd && (
          <div className="form-adornment form-adornment--end controlled-number-field__adornment controlled-number-field__adornment--end">
            {endAdornment}
          </div>
        )}
      </div>

      {helperText && <span className="form-field__helper">{helperText}</span>}
      {error && <span className="form-field__error">{error.message}</span>}
    </div>
  );
}
